import styled from 'styled-components';

import Button from 'components/Button';
import Image from 'components/Image';

import useInView from 'hooks/useInView';
import useIsolateNav from 'src/hooks/useIsolateNav';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import { Grid, columns } from 'styles/grid';
import TYPO, { H5, P1, rem, weight, TEXT_INDENT } from 'styles/typography';

import { AWS_S3_BUCKET_URL } from 'utils/constants';
import { isExternalLink, formatSlug } from 'utils/helpers';

import PromotionsBanner from './PromotionsBanner';

const Hero = styled.section`
  height: 540px;
  background: ${({ bgImg }) => (bgImg ? `url(${bgImg.mobile})` : 'none')}
    no-repeat center;
  background-size: cover;

  ${BREAKPOINTS.small`
    height: ${({ hasPromoBanner }) =>
      hasPromoBanner ? 'calc(100vh - 91px)' : '100vh'};
    min-height: 550px;
    max-height: ${({ hasPromoBanner }) => (hasPromoBanner ? ' 810px' : 'none')};
    background-image: ${({ bgImg }) => (bgImg ? `url(${bgImg.url})` : 'none')};
  `}
`;

const Overlay = styled.div`
  position: relative;
  padding: 30px 15px 55px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  height: inherit;
  min-height: inherit;
  max-height: inherit;
  color: ${COLORS.pinkMuted};
  text-align: center;
  background: ${({ hasPromoBanner }) =>
    hasPromoBanner
      ? `linear-gradient(180deg, ${COLORS.forestDark.opacity(0.5)} 50%, ${
          COLORS.forestDark
        } 100%)`
      : COLORS.black.opacity(0.4)};

  ${BREAKPOINTS.small`
    padding: ${({ hasPromoBanner }) =>
      hasPromoBanner
        ? '0 var(--gutter-side) 107px'
        : '75px var(--gutter-side) 30px'};
    justify-content: ${({ hasPromoBanner }) =>
      hasPromoBanner ? 'flex-end' : 'center'};
  `}
`;

const BackgroundVideo = styled.video`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: inherit;
  min-height: inherit;
  max-height: inherit;
  object-fit: cover;
`;

const Subtitle = styled.h1`
  ${TYPO.p3}
  margin-bottom: 10px;

  ${BREAKPOINTS.small`
    margin-bottom: 20px;
  `}
`;

const Title = styled.h2`
  margin: 0 auto;
  font-size: ${rem(70)};
  line-height: ${rem(75)};
  font-weight: ${weight.black};
  max-width: 700px;
  letter-spacing: -0.02em;

  ${BREAKPOINTS.medium`
    max-width: 1000px;
    font-size: ${rem(104)};
    line-height: ${rem(97)};
  `}

  ${BREAKPOINTS.large`
    font-size: ${rem(120)};
    line-height: ${rem(120)};
    letter-spacing: -0.03em;
  `}
`;

const Section = styled.section`
  padding: 50px 30px 100px;
  background-color: ${COLORS.pinkMillennial};
  background-image: ${({ hasPromoBanner }) =>
    hasPromoBanner
      ? 'url(/images/backgrounds/bg-dots-pattern-forest.svg)'
      : 'none'};
  background-size: 54px auto;
  color: ${COLORS.forest};

  ${BREAKPOINTS.small`
    padding: 50px 0 100px;
    background-size: 63px auto;
  `}

  ${BREAKPOINTS.medium`
    padding: 98px 0 150px;
  `}
`;

const Card = styled(Grid)`
  margin-bottom: 32px;
  padding: 0;

  &:last-child {
    margin-bottom: 0;
  }

  ${BREAKPOINTS.small`
    margin-bottom: 60px;
    display: flex;
  `}

  ${BREAKPOINTS.medium`
    margin-bottom: 100px;
  `}
`;

const CardInner = styled.div`
  ${columns(null, 12)}
  margin-bottom: 20px;
  position: relative;

  ${BREAKPOINTS.small`
    margin-bottom: 0;

    ${Card}:nth-child(odd) & {
      ${columns(2, 5)}
    }

    ${Card}:nth-child(even) & {
      ${columns(7, 5)}
    }
  `}

  ${BREAKPOINTS.medium`
    margin-right: 40px;

    ${Card}:nth-child(odd) & {
      margin: 0 40px 0 0;
    }

    ${Card}:nth-child(even) & {
      margin: 0 0 0 40px;
    }
  `}
`;

const CardImage = styled(Image)`
  max-height: 220px;

  ${BREAKPOINTS.small`
    max-height: 300px;
  `}
`;

const CardContent = styled.div`
  ${columns(null, 12)}

  ${BREAKPOINTS.small`
    ${columns(7, 5)}
    padding-left: 40px;
    max-width: 460px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    justify-self: start;

    ${Card}:nth-child(even) & {
      ${columns(2, 5)}
      padding: 0 40px 0 0;
      justify-self: end;
    }
  `}
`;

const A = styled.a`
  ${TYPO.p1}
  margin: 15px 0 20px;
  display: block;
  color: ${COLORS.redWine};
  font-weight: ${weight.semibold};
  text-indent: ${TEXT_INDENT};

  ${BREAKPOINTS.small`
    margin: 15px 0 25px;
  `};
`;

const ButtonStyled = styled(Button)`
  background-color: ${COLORS.redWine};
  align-self: flex-start;

  &:hover {
    background-color: ${COLORS.redWine.mix(COLORS.black, 0.2)};
  }
`;

const EventsAndPackages = ({ page }) => {
  const [inViewRef, inView] = useInView(false);
  useIsolateNav(inView);

  const hasPromoBanner = !!page.promotionsBanner;
  return (
    <>
      <Hero
        ref={inViewRef}
        bgImg={page.heroAsset}
        hasPromoBanner={hasPromoBanner}
      >
        {!page.heroAsset && (
          <BackgroundVideo muted playsInline autoPlay loop>
            <source
              src={`${AWS_S3_BUCKET_URL}/clays-events-video-reel-v2.mp4`}
              type="video/mp4"
            />
          </BackgroundVideo>
        )}

        <Overlay hasPromoBanner={hasPromoBanner}>
          <Subtitle>{page.subtitle}</Subtitle>
          <Title>{page.title}</Title>
        </Overlay>
      </Hero>

      {hasPromoBanner && (
        <PromotionsBanner
          data={page.promotionsBanner}
          claysLocation={page.claysLocation}
        />
      )}

      <Section hasPromoBanner={hasPromoBanner}>
        {page.packagesItems?.map(
          (
            {
              title,
              description,
              image,
              packagesLabel,
              packagesPdf,
              enquireLink,
              enquireLabel,
            },
            i
          ) => (
            <Card key={i}>
              <CardInner>
                <CardImage image={image} />
              </CardInner>
              <CardContent>
                <H5 as="h3" small weight="bold">
                  {title}
                </H5>
                <P1 indent>{description}</P1>
                <A href={packagesPdf?.url} target="_blank" rel="noreferrer">
                  {packagesLabel}
                </A>
                {enquireLink && (
                  <ButtonStyled
                    href={
                      isExternalLink(enquireLink)
                        ? enquireLink
                        : `/${formatSlug(page.claysLocation)}${enquireLink}`
                    }
                  >
                    {enquireLabel}
                  </ButtonStyled>
                )}
              </CardContent>
            </Card>
          )
        )}
      </Section>
    </>
  );
};

export default EventsAndPackages;
